import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Pagination,
  Dialog, DialogContent, TextField, InputAdornment, Avatar, Select, MenuItem,
  Grid, Divider, IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import adminAxios from '../../utils/adminAxios';
import { dinero } from '../../utils/dinero';

const LIMIT = 8;

// ---- Catálogos de presentación (colores del tema) ----
const CATEGORIA = {
  soporte:    { label: 'Soporte',    bg: (t) => alpha(t.palette.success.main, 0.2), color: 'success.oscuro' },
  reporte:    { label: 'Reporte',    bg: 'error.main', color: 'white' },
  aclaracion: { label: 'Aclaración', bg: (t) => alpha(t.palette.secondary.main, 0.12), color: 'primary.main' },
};
const PRIORIDAD = {
  alta:  { label: 'Alta',  color: 'error.oscuro' },
  media: { label: 'Media', color: 'warning.oscuro' },
  baja:  { label: 'Baja',  color: 'success.oscuro' },
};
const ESTADO = {
  abierto:     { label: 'Abierto',     bg: (t) => alpha(t.palette.secondary.main, 0.15), color: 'primary.main' },
  en_revision: { label: 'En revisión', bg: 'primary.main', color: 'white' },
  resuelto:    { label: 'Resuelto',    bg: (t) => alpha(t.palette.success.main, 0.2), color: 'success.oscuro' },
  cerrado:     { label: 'Cerrado',     bg: 'sutil', color: 'text.secondary' },
};

const nombreDe = (u, fallback = '—') =>
  (u ? `${u.name || ''} ${u.apellido || ''}`.trim() || u.email : fallback) || fallback;

const iniciales = (nombre = '') =>
  nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';

const fechaCorta = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const fechaHora = (iso) =>
  iso ? new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

// ---- Tarjeta de resumen ----
const StatCard = ({ icono, titulo, valor, sub, color, tint }) => (
  <Box sx={{ flex: 1, minWidth: 190, bgcolor: 'white', borderRadius: 3, border: 1, borderColor: 'sutil', p: 2.5 }}>
    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', mb: 1.5 }}>
      {titulo}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
        {icono}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>{valor}</Typography>
        {sub && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// En tickets ya resueltos/cerrados la prioridad se muestra en gris sutil: la info sigue
// ahí pero sin los colores fuertes que estorban cuando el caso ya terminó.
const Prioridad = ({ p, muted }) => {
  const cfg = PRIORIDAD[p] || PRIORIDAD.media;
  const color = muted ? 'text.secondary' : cfg.color;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: 13, fontWeight: muted ? 500 : 600, color }}>{cfg.label}</Typography>
    </Box>
  );
};

const esFinalizado = (estado) => estado === 'resuelto' || estado === 'cerrado';

const ChipCategoria = ({ c }) => {
  const cfg = CATEGORIA[c] || CATEGORIA.soporte;
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, borderRadius: '8px' }} />;
};

const ChipEstado = ({ e }) => {
  const cfg = ESTADO[e] || ESTADO.abierto;
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, borderRadius: '8px' }} />;
};

const Tickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, abiertos: 0, enRevision: 0, resueltos: 0, altaPrioridad: 0 });
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);

  const [tab, setTab] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [page, setPage] = useState(1);

  // Modal de detalle
  const [detalle, setDetalle] = useState(null); // ticket completo
  const [pago, setPago] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [respuesta, setRespuesta] = useState('');
  const [respArchivos, setRespArchivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef(null);
  const [estadoEdit, setEstadoEdit] = useState('abierto');
  const [prioridadEdit, setPrioridadEdit] = useState('media');
  const [asignadoEdit, setAsignadoEdit] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(null); // { tipo:'fallo'|'novedad', fallo? }
  const [novedadMsg, setNovedadMsg] = useState('');
  const [procDisputa, setProcDisputa] = useState(false);

  // Nuevo ticket
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [nuevo, setNuevo] = useState({ categoria: 'soporte', prioridad: 'media', asunto: '', descripcion: '', solicitanteNombre: '', solicitanteEmail: '' });
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(() =>
    adminAxios.get('/api/tickets')
      .then(res => { setTickets(res.data.tickets || []); setStats(res.data.stats || {}); })
      .catch(() => setToast({ tipo: 'error', msg: 'No se pudieron cargar los tickets.' }))
  , []);

  useEffect(() => { cargar().finally(() => setCargando(false)); }, [cargar]);
  useEffect(() => { setPage(1); }, [tab, busqueda, filtroEstado, filtroPrioridad]);

  const enTab = useCallback((t) =>
    tab === 'todos' ? true
      : tab === 'soporte' ? t.categoria === 'soporte'
      : ['reporte', 'aclaracion'].includes(t.categoria)
  , [tab]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return tickets.filter(t => {
      if (!enTab(t)) return false;
      if (filtroEstado && t.estado !== filtroEstado) return false;
      if (filtroPrioridad && t.prioridad !== filtroPrioridad) return false;
      if (q) {
        const solicitante = `${nombreDe(t.solicitante, t.solicitanteNombre)} ${t.solicitante?.email || t.solicitanteEmail || ''}`.toLowerCase();
        if (!(`${t.numero} ${t.asunto} ${solicitante}`.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [tickets, enTab, filtroEstado, filtroPrioridad, busqueda]);

  const activo = (e) => e === 'abierto' || e === 'en_revision';
  const badges = useMemo(() => ({
    todos: tickets.filter(t => activo(t.estado)).length,
    soporte: tickets.filter(t => t.categoria === 'soporte' && activo(t.estado)).length,
    reportes: tickets.filter(t => ['reporte', 'aclaracion'].includes(t.categoria) && activo(t.estado)).length,
  }), [tickets]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / LIMIT));
  const inicio = (page - 1) * LIMIT;
  const pagina = filtrados.slice(inicio, inicio + LIMIT);

  const TABS = [
    { key: 'todos', label: 'Todos', badge: badges.todos },
    { key: 'soporte', label: 'Soporte', badge: badges.soporte },
    { key: 'reportes', label: 'Reportes o aclaraciones', badge: badges.reportes },
  ];

  // ---- Abrir / refrescar detalle ----
  const abrirDetalle = async (id) => {
    setCargandoDetalle(true);
    setDetalle({ _id: id }); // abre el modal en estado de carga
    try {
      const { data } = await adminAxios.get(`/api/tickets/${id}`);
      setDetalle(data.ticket);
      setPago(data.pago || null);
      setEstadoEdit(data.ticket.estado);
      setPrioridadEdit(data.ticket.prioridad);
      setAsignadoEdit(data.ticket.asignadoA || '');
      setRespuesta('');
    } catch {
      setToast({ tipo: 'error', msg: 'No se pudo abrir el ticket.' });
      setDetalle(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const cerrarDetalle = () => { setDetalle(null); setPago(null); setConfirmar(null); };

  const enviarRespuesta = async () => {
    if (!respuesta.trim() && respArchivos.length === 0) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('texto', respuesta.trim());
      respArchivos.forEach(a => fd.append('adjuntos', a));
      const { data } = await adminAxios.post(`/api/tickets/${detalle._id}/mensajes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDetalle(prev => ({ ...prev, mensajes: data.mensajes, estado: data.estado }));
      setEstadoEdit(data.estado);
      setRespuesta(''); setRespArchivos([]);
      cargar();
      setToast({ tipo: 'success', msg: 'Respuesta enviada al solicitante.' });
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo enviar la respuesta.' });
    } finally {
      setEnviando(false);
    }
  };

  const onPickResp = (e) => {
    const nuevos = Array.from(e.target.files || []);
    setRespArchivos(prev => [...prev, ...nuevos].slice(0, 5));
    e.target.value = '';
  };

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      const { data } = await adminAxios.patch(`/api/tickets/${detalle._id}`, {
        estado: estadoEdit, prioridad: prioridadEdit, asignadoA: asignadoEdit
      });
      setDetalle(prev => ({ ...prev, ...data }));
      cargar();
      setToast({ tipo: 'success', msg: 'Cambios guardados.' });
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudieron guardar los cambios.' });
    } finally {
      setGuardando(false);
    }
  };

  // ---- Resolución financiera de la disputa (reusa endpoints existentes) ----
  const ejecutarDisputa = async () => {
    const bookingId = detalle?.booking?._id;
    if (!bookingId || !confirmar) return;
    setProcDisputa(true);
    try {
      if (confirmar.tipo === 'novedad') {
        await adminAxios.patch(`/api/admin/disputas/${bookingId}/novedad`, { mensaje: novedadMsg });
        setToast({ tipo: 'success', msg: 'Novedad enviada al solicitante.' });
      } else {
        await adminAxios.patch(`/api/admin/disputas/${bookingId}/resolver`, { fallo: confirmar.fallo });
        await adminAxios.patch(`/api/tickets/${detalle._id}`, { estado: 'resuelto' });
        setToast({
          tipo: 'success',
          msg: confirmar.fallo === 'proveedor'
            ? 'Disputa a favor del proveedor: el servicio quedó completado y su pago pasa a "Por dispersar".'
            : 'Disputa a favor del cliente: su pago se reembolsa (tarjeta automático; SPEI/tienda a Devoluciones).'
        });
        await abrirDetalle(detalle._id);
      }
      setConfirmar(null);
      cargar();
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo completar la operación.' });
    } finally {
      setProcDisputa(false);
    }
  };

  const crearTicket = async () => {
    if (!nuevo.asunto.trim()) { setToast({ tipo: 'error', msg: 'El asunto es obligatorio.' }); return; }
    setCreando(true);
    try {
      await adminAxios.post('/api/tickets', nuevo);
      await cargar();
      setNuevoOpen(false);
      setNuevo({ categoria: 'soporte', prioridad: 'media', asunto: '', descripcion: '', solicitanteNombre: '', solicitanteEmail: '' });
      setToast({ tipo: 'success', msg: 'Ticket creado.' });
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo crear el ticket.' });
    } finally {
      setCreando(false);
    }
  };

  const cabeceraSx = { color: 'text.secondary', fontWeight: 700, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', borderBottom: 1, borderColor: 'sutil' };
  const celdaSx = { borderBottom: 1, borderColor: 'sutil', py: 1.75 };
  const selectSx = { bgcolor: 'white', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } };

  // Ticket finalizado (resuelto o cerrado): se oculta el form de respuesta para no contestar por error
  const finalizado = esFinalizado(detalle?.estado);
  // Disputa abierta con dinero por resolver dentro del modal
  const disputaAbierta = detalle?.origen === 'disputa' && detalle?.booking?.disputa?.abierta;
  const evidencias = detalle?.booking?.disputa?.evidencias?.length
    ? detalle.booking.disputa.evidencias
    : (detalle?.booking?.aclaracion?.evidencias || []);

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Botón nuevo ticket */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNuevoOpen(true)}
          sx={{ bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
          Nuevo ticket
        </Button>
      </Box>

      {/* Tarjetas de resumen */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatCard titulo="Total tickets" valor={stats.total} sub="registrados" tint={(t) => alpha(t.palette.secondary.main, 0.12)} color="primary.main" icono={<ChatBubbleOutlineOutlinedIcon />} />
        <StatCard titulo="Abiertos" valor={stats.abiertos} sub="sin atender" tint={(t) => alpha(t.palette.secondary.main, 0.15)} color="primary.main" icono={<AccessTimeOutlinedIcon />} />
        <StatCard titulo="En revisión" valor={stats.enRevision} sub="en proceso" tint={(t) => alpha(t.palette.secondary.main, 0.12)} color="primary.main" icono={<VisibilityOutlinedIcon />} />
        <StatCard titulo="Resueltos" valor={stats.resueltos} sub="este período" tint={(t) => alpha(t.palette.success.main, 0.2)} color="success.oscuro" icono={<CheckCircleOutlineIcon />} />
        <StatCard titulo="Alta prioridad" valor={stats.altaPrioridad} sub="pendientes" tint={(t) => alpha(t.palette.error.main, 0.2)} color="error.oscuro" icono={<ErrorOutlineIcon />} />
      </Box>

      {/* Panel principal */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: 1, borderColor: 'sutil', overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 3, px: 3, pt: 2, borderBottom: 1, borderColor: 'sutil', flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <Box key={t.key} onClick={() => setTab(t.key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, pb: 1.5, cursor: 'pointer',
                borderBottom: 3, borderColor: tab === t.key ? 'primary.main' : 'transparent',
                color: tab === t.key ? 'primary.main' : 'text.secondary', fontWeight: tab === t.key ? 700 : 500,
              }}>
              <Typography sx={{ fontSize: 14, fontWeight: 'inherit', color: 'inherit' }}>{t.label}</Typography>
              <Chip label={t.badge} size="small" sx={{ height: 20, minWidth: 20, bgcolor: tab === t.key ? 'primary.main' : 'sutil', color: tab === t.key ? 'white' : 'text.secondary', fontWeight: 700, fontSize: 11 }} />
            </Box>
          ))}
        </Box>

        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 2.5, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Buscar por número, asunto o solicitante…"
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            sx={{ flex: 1, minWidth: 260, ...selectSx }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          />
          <Select size="small" displayEmpty value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} sx={{ minWidth: 150, ...selectSx }}>
            <MenuItem value="">Todos los estados</MenuItem>
            {Object.keys(ESTADO).map(k => <MenuItem key={k} value={k}>{ESTADO[k].label}</MenuItem>)}
          </Select>
          <Select size="small" displayEmpty value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)} sx={{ minWidth: 150, ...selectSx }}>
            <MenuItem value="">Toda prioridad</MenuItem>
            {Object.keys(PRIORIDAD).map(k => <MenuItem key={k} value={k}>{PRIORIDAD[k].label}</MenuItem>)}
          </Select>
          <Typography sx={{ ml: 'auto', fontSize: 13, color: 'text.secondary' }}>{filtrados.length} de {tickets.length} tickets</Typography>
        </Box>

        {/* Tabla */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={cabeceraSx}>Ticket</TableCell>
                <TableCell sx={cabeceraSx}>Categoría</TableCell>
                <TableCell sx={cabeceraSx}>Asunto</TableCell>
                <TableCell sx={cabeceraSx}>Solicitante</TableCell>
                <TableCell sx={cabeceraSx}>Prioridad</TableCell>
                <TableCell sx={cabeceraSx}>Estado</TableCell>
                <TableCell sx={cabeceraSx}>Fecha</TableCell>
                <TableCell sx={cabeceraSx} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagina.map(t => {
                const solNombre = nombreDe(t.solicitante, t.solicitanteNombre);
                const solEmail = t.solicitante?.email || t.solicitanteEmail || '';
                return (
                  <TableRow key={t._id} hover>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'primary.main' }}>{t.numero}</Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}><ChipCategoria c={t.categoria} /></TableCell>
                    <TableCell sx={{ ...celdaSx, maxWidth: 320 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'primary.main' }}>{t.asunto}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                        {(t.mensajes?.length || 0)} mensaje{(t.mensajes?.length || 0) === 1 ? '' : 's'}
                        {t.asignadoA ? ` · ${t.asignadoA}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={celdaSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'secondary.main', color: 'primary.main' }}>{iniciales(solNombre)}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>{solNombre}</Typography>
                          <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary' }}>{solEmail}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={celdaSx}><Prioridad p={t.prioridad} muted={esFinalizado(t.estado)} /></TableCell>
                    <TableCell sx={celdaSx}><ChipEstado e={t.estado} /></TableCell>
                    <TableCell sx={celdaSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography sx={{ fontSize: 12.5 }}>{fechaCorta(t.createdAt)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={celdaSx} align="right">
                      <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => abrirDetalle(t._id)}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider', color: 'text.secondary' }}>
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {pagina.length === 0 && (
                <TableRow><TableCell colSpan={8} sx={{ ...celdaSx, textAlign: 'center', color: 'text.secondary', py: 5 }}>
                  No hay tickets que coincidan.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderTop: 1, borderColor: 'sutil', flexWrap: 'wrap', gap: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Mostrando {filtrados.length} resultado{filtrados.length === 1 ? '' : 's'}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Pagination count={totalPaginas} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" size="small" />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`Abierto ${stats.abiertos}`} size="small" sx={{ bgcolor: ESTADO.abierto.bg, color: ESTADO.abierto.color, fontWeight: 700 }} />
              <Chip label={`En revisión ${stats.enRevision}`} size="small" sx={{ bgcolor: ESTADO.en_revision.bg, color: ESTADO.en_revision.color, fontWeight: 700 }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ---------- Modal de detalle ---------- */}
      <Dialog open={!!detalle} onClose={cerrarDetalle} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {cargandoDetalle || !detalle?.numero ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <DialogContent sx={{ p: 0 }}>
            {/* Encabezado */}
            <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'sutil' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary' }}>{detalle.numero}</Typography>
                <ChipCategoria c={detalle.categoria} />
                <Prioridad p={detalle.prioridad} muted={esFinalizado(detalle.estado)} />
                <IconButton size="small" onClick={cerrarDetalle} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>{detalle.asunto}</Typography>
              {detalle.origen === 'chat' && detalle.reportado && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Persona reportada: <strong>{nombreDe(detalle.reportado)}</strong>
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => navigate(`/admin/personas?id=${detalle.reportado._id}`)}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider', color: 'text.secondary' }}>
                    Ver en Personas
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container>
              {/* Columna principal */}
              <Grid size={{ xs: 12, md: 7.5 }} sx={{ p: 3, borderRight: { md: 1 }, borderColor: 'sutil' }}>
                {detalle.descripcion && (
                  <Box sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.05), borderRadius: 2, p: 2, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>Descripción del ticket</Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{detalle.descripcion}</Typography>
                  </Box>
                )}

                {/* Evidencias (reportes / aclaraciones de reserva) */}
                {evidencias.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                    {evidencias.map((ev, i) => (
                      <Chip key={i} component="a" href={ev.url} target="_blank" rel="noopener noreferrer" clickable
                        icon={<AttachFileIcon sx={{ fontSize: 14 }} />} label={ev.nombre || `Evidencia ${i + 1}`} size="small"
                        sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'primary.main', fontWeight: 600, fontSize: 11, maxWidth: 180, '& .MuiChip-icon': { color: 'secondary.main' } }} />
                    ))}
                  </Box>
                )}

                {/* Hilo de mensajes */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                  {(detalle.mensajes || []).map((m, i) => (
                    <Box key={i} sx={{ alignSelf: m.esAdmin ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5, textAlign: m.esAdmin ? 'right' : 'left' }}>
                        <Box component="strong" sx={{ color: 'primary.main' }}>{m.esAdmin ? (m.autorNombre || 'Administrador') : (m.autorNombre || nombreDe(detalle.solicitante, detalle.solicitanteNombre))}</Box>
                        {'  '}{fechaHora(m.fecha)}
                      </Typography>
                      <Box sx={{ bgcolor: m.esAdmin ? 'primary.main' : 'sutil', color: m.esAdmin ? 'white' : 'text.primary', p: 1.5, borderRadius: 2, fontSize: 14 }}>
                        {m.texto}
                        {m.adjuntos?.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: m.texto ? 0.75 : 0 }}>
                            {m.adjuntos.map((a, j) => (
                              <Chip key={j} component="a" href={a.url} target="_blank" rel="noopener noreferrer" clickable
                                icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />} label={a.nombre || `Archivo ${j + 1}`} size="small"
                                sx={{ bgcolor: m.esAdmin ? 'rgba(255,255,255,0.2)' : ((t) => alpha(t.palette.secondary.main, 0.12)), color: m.esAdmin ? 'white' : 'primary.main', fontWeight: 600, fontSize: 11, maxWidth: 170, '& .MuiChip-icon': { color: m.esAdmin ? 'white' : 'secondary.main' } }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                  {(detalle.mensajes || []).length === 0 && (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Sin mensajes todavía.</Typography>
                  )}
                </Box>

                {/* Resolución financiera (disputa abierta) */}
                {disputaAbierta && (
                  <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: 2, p: 2, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'warning.oscuro', mb: 0.5 }}>Resolver disputa</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
                      Pago en custodia: <strong>{dinero(pago?.monto)}</strong>. Resuelve para liberar o reembolsar el dinero.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="contained" disableElevation color="success"
                        onClick={() => setConfirmar({ tipo: 'fallo', fallo: 'proveedor' })}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, color: 'white' }}>
                        Liberar al proveedor
                      </Button>
                      <Button size="small" variant="contained" disableElevation color="error"
                        onClick={() => setConfirmar({ tipo: 'fallo', fallo: 'cliente' })}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, color: 'white' }}>
                        Reembolsar al cliente
                      </Button>
                      <Button size="small" variant="text"
                        onClick={() => { setNovedadMsg('Te enviamos a tu correo documentación necesaria para proceder con el caso.'); setConfirmar({ tipo: 'novedad' }); }}
                        sx={{ textTransform: 'none', fontWeight: 600, color: 'warning.oscuro' }}>
                        Enviar novedad
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Responder — oculto cuando el ticket está resuelto o cerrado para no contestar por error */}
                {finalizado ? (
                  <Box sx={{ bgcolor: 'sutil', borderRadius: 2, p: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      Este ticket está {(ESTADO[detalle.estado]?.label || 'finalizado').toLowerCase()}. Para responder, cámbialo de estado y guarda los cambios.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {respArchivos.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {respArchivos.map((a, i) => (
                          <Chip key={i} label={a.name} size="small" onDelete={() => setRespArchivos(prev => prev.filter((_, j) => j !== i))}
                            sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'primary.main', fontWeight: 600, fontSize: 11, maxWidth: 180 }} />
                        ))}
                      </Box>
                    )}
                    <TextField fullWidth multiline minRows={2} placeholder="Escribe una respuesta al solicitante…"
                      value={respuesta} onChange={(e) => setRespuesta(e.target.value)} sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input ref={fileRef} type="file" hidden multiple accept="image/*,application/pdf" onChange={onPickResp} />
                      <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => fileRef.current?.click()}
                        sx={{ textTransform: 'none', borderRadius: 2, borderColor: 'divider', color: 'text.secondary' }}>
                        Adjuntar
                      </Button>
                      <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={enviarRespuesta}
                        disabled={enviando || (!respuesta.trim() && respArchivos.length === 0)}
                        sx={{ ml: 'auto', bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
                        {enviando ? 'Enviando…' : 'Enviar respuesta'}
                      </Button>
                    </Box>
                  </>
                )}
              </Grid>

              {/* Sidebar */}
              <Grid size={{ xs: 12, md: 4.5 }} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, mb: 1 }}>SOLICITANTE</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
                  <Avatar sx={{ width: 38, height: 38, fontSize: 14, bgcolor: 'secondary.main', color: 'primary.main' }}>{iniciales(nombreDe(detalle.solicitante, detalle.solicitanteNombre))}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main' }}>{nombreDe(detalle.solicitante, detalle.solicitanteNombre)}</Typography>
                    <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{detalle.solicitante?.email || detalle.solicitanteEmail || '—'}</Typography>
                  </Box>
                </Box>

                {detalle.booking && (
                  <Box sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), borderRadius: 2, p: 1.5, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>RESERVA LIGADA</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main' }}>{detalle.booking.servicioNombre}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{detalle.booking.fecha} {detalle.booking.hora} · {detalle.booking.status}</Typography>
                  </Box>
                )}

                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, mb: 0.75 }}>ESTADO</Typography>
                <Select fullWidth size="small" value={estadoEdit} onChange={(e) => setEstadoEdit(e.target.value)} sx={{ mb: 2, ...selectSx }}>
                  {Object.keys(ESTADO).map(k => <MenuItem key={k} value={k}>{ESTADO[k].label}</MenuItem>)}
                </Select>

                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, mb: 0.75 }}>PRIORIDAD</Typography>
                <Select fullWidth size="small" value={prioridadEdit} onChange={(e) => setPrioridadEdit(e.target.value)} sx={{ mb: 2, ...selectSx }}>
                  {Object.keys(PRIORIDAD).map(k => <MenuItem key={k} value={k}>{PRIORIDAD[k].label}</MenuItem>)}
                </Select>

                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, mb: 0.75 }}>ASIGNADO A</Typography>
                <TextField fullWidth size="small" placeholder="Sin asignar" value={asignadoEdit} onChange={(e) => setAsignadoEdit(e.target.value)} sx={{ mb: 2, ...selectSx }} />

                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, mb: 0.75 }}>FECHAS</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Creado</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>{fechaCorta(detalle.createdAt)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Actualizado</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>{fechaCorta(detalle.updatedAt)}</Typography>
                </Box>

                <Button fullWidth variant="contained" onClick={guardarCambios} disabled={guardando}
                  sx={{ bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
                  {guardando ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        )}
      </Dialog>

      {/* Confirmación de la resolución financiera */}
      <Dialog open={!!confirmar} onClose={() => !procDisputa && setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ mb: 1 }}>
            {confirmar?.tipo === 'novedad' ? 'Enviar novedad al solicitante'
              : confirmar?.fallo === 'proveedor' ? 'Resolver a favor del proveedor'
              : 'Resolver a favor del cliente'}
          </Typography>
          {confirmar?.tipo === 'novedad' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se notifica al solicitante sin cerrar la disputa (sigue congelado el pago).
              </Typography>
              <TextField fullWidth multiline minRows={3} value={novedadMsg} onChange={(e) => setNovedadMsg(e.target.value)} label="Mensaje" sx={{ mb: 3 }} />
            </>
          ) : confirmar?.fallo === 'proveedor' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              El servicio quedará <strong>completado</strong> y el pago pasará a "Por dispersar". Ambas partes serán notificadas.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              La reserva quedará <strong>cancelada</strong> y se reembolsará el pago completo (tarjeta: automático; SPEI/tienda: a Devoluciones). Ambas partes serán notificadas.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => setConfirmar(null)} disabled={procDisputa} sx={{ color: 'text.secondary', textTransform: 'none' }}>Cancelar</Button>
            <Button variant="contained" onClick={ejecutarDisputa} disabled={procDisputa || (confirmar?.tipo === 'novedad' && !novedadMsg.trim())}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              {procDisputa ? 'Procesando…' : confirmar?.tipo === 'novedad' ? 'Enviar novedad' : 'Confirmar'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Nuevo ticket */}
      <Dialog open={nuevoOpen} onClose={() => !creando && setNuevoOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ mb: 2 }}>Nuevo ticket</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>Categoría</Typography>
              <Select fullWidth size="small" value={nuevo.categoria} onChange={(e) => setNuevo({ ...nuevo, categoria: e.target.value })} sx={selectSx}>
                {Object.keys(CATEGORIA).map(k => <MenuItem key={k} value={k}>{CATEGORIA[k].label}</MenuItem>)}
              </Select>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>Prioridad</Typography>
              <Select fullWidth size="small" value={nuevo.prioridad} onChange={(e) => setNuevo({ ...nuevo, prioridad: e.target.value })} sx={selectSx}>
                {Object.keys(PRIORIDAD).map(k => <MenuItem key={k} value={k}>{PRIORIDAD[k].label}</MenuItem>)}
              </Select>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Asunto" value={nuevo.asunto} onChange={(e) => setNuevo({ ...nuevo, asunto: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Nombre del solicitante" value={nuevo.solicitanteNombre} onChange={(e) => setNuevo({ ...nuevo, solicitanteNombre: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Email del solicitante" value={nuevo.solicitanteEmail} onChange={(e) => setNuevo({ ...nuevo, solicitanteEmail: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline minRows={3} label="Descripción" value={nuevo.descripcion} onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={() => setNuevoOpen(false)} disabled={creando} sx={{ color: 'text.secondary', textTransform: 'none' }}>Cancelar</Button>
            <Button variant="contained" onClick={crearTicket} disabled={creando || !nuevo.asunto.trim()}
              sx={{ bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
              {creando ? 'Creando…' : 'Crear ticket'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Tickets;
